/**
 * FormattedContent — renders markdown/plain-text content with Tailwind prose styling.
 *
 * react-markdown v10+ and remark-gfm v4+ are Pure ESM with internal circular
 * dependencies that cause TDZ errors in Vite/Rollup production builds. They are
 * lazy-loaded here so they live in a separate chunk, breaking the circular reference
 * from the main bundle. Callers require no changes.
 */

import { lazy, Suspense } from "react";

// ── Lazy markdown renderer ──────────────────────────────────────────
// Both packages loaded together in a single dynamic import so they
// share one network request and initialize in the correct order.
const MarkdownBlock = lazy(() =>
  Promise.all([import("react-markdown"), import("remark-gfm")]).then(
    ([{ default: ReactMarkdown }, { default: remarkGfm }]) => ({
      default: function MarkdownBlock({ children, components }) {
        return (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {children}
          </ReactMarkdown>
        );
      },
    })
  )
);

/* ── Executive Summary extraction ──────────────────────────────── */
function extractSummary(content) {
  if (!content) return { summary: null, body: content };

  const hrIdx = content.indexOf("\n---\n");
  if (hrIdx > 0 && hrIdx < 800) {
    return {
      summary: content.slice(0, hrIdx).trim(),
      body: content.slice(hrIdx + 5).trim(),
    };
  }

  const paraIdx = content.indexOf("\n\n");
  if (paraIdx > 0 && paraIdx < 600) {
    return {
      summary: content.slice(0, paraIdx).trim(),
      body: content.slice(paraIdx + 2).trim(),
    };
  }

  return { summary: null, body: content };
}

/* ── Custom renderers for Tailwind styling ─────────────────────── */
function getComponents(compact) {
  const base = compact ? "text-xs" : "text-sm";
  return {
    h1: ({ children }) => <h1 className={`${base} font-bold text-gray-900 mt-4 mb-2`}>{children}</h1>,
    h2: ({ children }) => <h2 className={`${base} font-bold text-gray-800 mt-3 mb-1.5`}>{children}</h2>,
    h3: ({ children }) => <h3 className={`${base} font-semibold text-gray-700 mt-2 mb-1`}>{children}</h3>,
    h4: ({ children }) => <h4 className={`${base} font-semibold text-gray-600 mt-2 mb-1`}>{children}</h4>,
    p: ({ children }) => <p className={`${base} text-gray-700 leading-relaxed mb-2`}>{children}</p>,
    ul: ({ children }) => <ul className="list-disc ml-5 space-y-0.5 mb-2">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal ml-5 space-y-0.5 mb-2">{children}</ol>,
    li: ({ children }) => <li className={`${base} text-gray-700`}>{children}</li>,
    strong: ({ children }) => <strong className="font-bold text-gray-900">{children}</strong>,
    em: ({ children }) => <em className="italic text-gray-600">{children}</em>,
    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
        {children}
      </a>
    ),
    hr: () => <hr className="my-3 border-gray-200" />,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-blue-300 pl-3 my-2 bg-blue-50/30 py-1 rounded-r">
        {children}
      </blockquote>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto mb-3">
        <table className="min-w-full text-xs border border-gray-200 rounded">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
    th: ({ children }) => <th className="px-2 py-1.5 text-left text-[10px] font-bold text-gray-600 uppercase border-b border-gray-200">{children}</th>,
    td: ({ children }) => <td className="px-2 py-1.5 text-xs text-gray-700 border-b border-gray-100">{children}</td>,
    code: ({ children, inline }) =>
      inline ? (
        <code className="text-[10px] bg-gray-100 px-1 py-0.5 rounded font-mono text-gray-800">{children}</code>
      ) : (
        <pre className="bg-gray-50 rounded p-3 overflow-x-auto mb-2">
          <code className="text-[10px] font-mono text-gray-700">{children}</code>
        </pre>
      ),
  };
}

/* ── Fallback while chunk loads ────────────────────────────────── */
function RawFallback({ content, compact }) {
  return (
    <pre className={`${compact ? "text-xs" : "text-sm"} text-gray-700 whitespace-pre-wrap leading-relaxed font-sans`}>
      {content}
    </pre>
  );
}

/* ── Main Component ────────────────────────────────────────────── */
export default function FormattedContent({ content, maxHeight = "600px", showSummary = false, compact = true }) {
  if (!content) return null;

  const { summary, body } = showSummary ? extractSummary(content) : { summary: null, body: content };
  const components = getComponents(compact);

  return (
    <div>
      {summary && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
          <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider mb-1">Executive Summary</p>
          <Suspense fallback={<RawFallback content={summary} compact={compact} />}>
            <MarkdownBlock components={components}>{summary}</MarkdownBlock>
          </Suspense>
        </div>
      )}

      <div className="overflow-y-auto" style={{ maxHeight }}>
        <Suspense fallback={<RawFallback content={body} compact={compact} />}>
          <MarkdownBlock components={components}>{body}</MarkdownBlock>
        </Suspense>
      </div>
    </div>
  );
}
